import { Layers } from "@/components/ui/icons";
import { Image, type ImageStyle } from "expo-image";
import { createContext, type ReactNode, useContext, useId, useState } from "react";
import { type StyleProp, View } from "react-native";

import { useReducedMotion } from "@/hooks/useReducedMotion";
import { useThemeColors } from "@/theme/theme";

/** Snapshot hosts wait for rendered art and suppress its loading fade. */
export const CardArtCaptureContext = createContext<((id: string) => void) | null>(null);

/**
 * Remote card art, for the surfaces that draw a lot of it at once.
 *
 * React Native's `Image` re-decodes on every mount and keeps no disk cache, so scrolling back up a
 * wall of tiles re-fetches art the device already had. `expo-image` caches to memory and disk,
 * decodes off the main thread, and holds the last frame while a new URI loads instead of flashing
 * empty.
 *
 * The fade is load feedback, not decoration: DESIGN.md §6 rules out decorative motion, and a 180ms
 * crossfade shows that art just arrived instead of popping a tile in mid-scroll. Under Reduce
 * Motion it is an instant cut.
 *
 * `contentFit` belongs to the caller. A card's art fills its 5:7 box (`cover`), while a sealed
 * product's packaging shot is fitted whole (`contain`) because the operator picks on that
 * silhouette. `DealLine` derives the choice from `product_type`.
 *
 * Geometry arrives as `style`, not `className`. NativeWind rewrites `className` into `style` only
 * for the components its interop map registers, which are React Native's own primitives, so a
 * Tailwind class on `expo-image`'s view is dropped and the art gets no width or height. Other
 * third-party views (the tab bar's `GlassView` / `BlurView`) are sized the
 * same way.
 */
export function CardArt(
  { uri, contentFit = "cover", style, accessibilityLabel, fallback }: {
    uri: string;
    /** `cover` fills the box (cards); `contain` fits the whole image (sealed product). */
    contentFit?: "cover" | "contain";
    /** Defaults to filling the box the caller has already sized. */
    style?: StyleProp<ImageStyle>;
    /** Omit on a fallback that merely repeats a name already beside it. */
    accessibilityLabel?: string;
    /** The caller's no-image treatment; defaults to a generic glyph. */
    fallback?: ReactNode;
  },
) {
  const capture = useContext(CardArtCaptureContext);
  const id = useId();
  const reduced = useReducedMotion();
  const c = useThemeColors();
  const [failedUri, setFailedUri] = useState<string | null>(null);
  if (failedUri === uri) {
    return fallback ?? (
      <View
        style={style ?? FILL_PARENT}
        className="items-center justify-center bg-surface-muted"
        aria-hidden
      >
        <Layers size={20} color={c.subtle} />
      </View>
    );
  }
  return (
    <Image
      source={{ uri }}
      onError={() => setFailedUri(uri)}
      style={style ?? FILL_PARENT}
      contentFit={contentFit}
      cachePolicy="memory-disk"
      transition={reduced || capture ? 0 : 180}
      onDisplay={capture ? () => capture(id) : undefined}
      accessible={accessibilityLabel !== undefined}
      // expo-image writes this label to the web `<img>`'s `alt`, and an `<img>` with no `alt` at
      // all is read as a file name, so unlabelled art passes `""` to mark itself decorative. On
      // native `accessible={false}` already keeps it out of the screen reader's focus order.
      accessibilityLabel={accessibilityLabel ?? ""}
    />
  );
}

const FILL_PARENT: ImageStyle = { width: "100%", height: "100%" };
